import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Crown, Shield, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useAppStore } from "../store/useAppStore";

type PlanId = "free" | "foundation" | "operator" | "atlas";

type PricingPlan = {
  id: PlanId;
  name: string;
  price: string;
  description: string;
  highlight: string;
  features: string[];
  accent: string;
  cta: string;
  recommended?: boolean;
};

const PLANS: PricingPlan[] = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    description: "A limited starter tier to test the workspace and see the value.",
    highlight: "Good for exploring the product",
    features: ["Basic lead management", "Pipeline preview only", "Limited AI access"],
    accent: "from-slate-100 to-slate-50",
    cta: "Stay on Free",
  },
  {
    id: "foundation",
    name: "Foundation",
    price: "$29.99/mo",
    description: "The easiest paid entry point for active operators.",
    highlight: "Best for solo investors getting started",
    features: ["Full follow-up workflow", "AI text generation", "Core pipeline features"],
    accent: "from-blue-100 to-cyan-50",
    cta: "Upgrade to Foundation",
    recommended: true,
  },
  {
    id: "operator",
    name: "Operator",
    price: "$59.99/mo",
    description: "More throughput for users who are already moving deals every week.",
    highlight: "For active teams and higher volume",
    features: ["Everything in Foundation", "Higher workflow capacity", "More automation headroom"],
    accent: "from-emerald-100 to-teal-50",
    cta: "Upgrade to Operator",
  },
  {
    id: "atlas",
    name: "Atlas",
    price: "$119.99/mo",
    description: "The most complete tier for heavy research and property intelligence.",
    highlight: "For power users who want the deepest workflow",
    features: ["Everything in Operator", "Deep data workflow", "Premium research capacity"],
    accent: "from-violet-100 to-fuchsia-50",
    cta: "Upgrade to Atlas",
  },
];

const COMPARISON_ROWS = [
  { label: "Lead management", free: "Basic", foundation: "Full", operator: "Full", atlas: "Full" },
  { label: "AI follow-up text", free: "Limited", foundation: "Included", operator: "Included", atlas: "Included" },
  { label: "Pipeline tools", free: "Preview only", foundation: "Full", operator: "Full", atlas: "Full" },
  { label: "Workflow capacity", free: "Low", foundation: "Moderate", operator: "High", atlas: "Highest" },
];

const PLAN_LABELS: Record<PlanId, string> = {
  free: "Free",
  foundation: "Foundation",
  operator: "Operator",
  atlas: "Atlas",
};

export default function Pricing() {
  const navigate = useNavigate();
  const { isAuthenticated, planId } = useAppStore();
  const [loadingPlan, setLoadingPlan] = useState<PlanId | null>(null);

  const activePlanLabel = useMemo(() => PLAN_LABELS[planId] || "Free", [planId]);

  const startCheckout = (targetPlan: PlanId) => {
    if (targetPlan === "free") {
      navigate(isAuthenticated ? "/" : "/login");
      return;
    }

    const checkoutRoute = `/checkout/${targetPlan}?returnTo=/pricing`;
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(checkoutRoute)}`);
      return;
    }

    setLoadingPlan(targetPlan);
    navigate(checkoutRoute, { state: { source: "pricing" } });
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.12),_transparent_30%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] text-slate-900">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-10 px-4 py-10 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
          <div className="grid gap-8 p-8 lg:grid-cols-[1.15fr_.85fr] lg:p-10">
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-slate-950 text-white hover:bg-slate-950">Billing</Badge>
                <Badge variant="outline" className="text-slate-600">Current plan: {activePlanLabel}</Badge>
              </div>
              <div className="space-y-3">
                <h1 className="max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">Choose the tier that matches your workflow</h1>
                <p className="max-w-2xl text-base leading-7 text-slate-600">
                  Start free, then move into Foundation, Operator, or Atlas when you want more automation, more capacity, and a stronger deal flow.
                  Every paid tier is connected to live Stripe checkout and webhook-based activation.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button className="bg-slate-950 text-white hover:bg-slate-800" onClick={() => navigate(isAuthenticated ? "/settings" : "/login?redirect=/pricing") }>
                    {isAuthenticated ? "Manage billing in Settings" : "Sign in to upgrade"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="outline" className="border-slate-200 bg-white hover:bg-slate-50" onClick={() => navigate("/")}>Back to app</Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50 p-5">
                <div className="mb-3 flex items-center gap-2 text-blue-700">
                  <Zap className="h-4 w-4" />
                  <span className="text-sm font-semibold">Fast upgrade path</span>
                </div>
                <p className="text-sm leading-6 text-slate-600">
                  Pick a plan, complete Stripe Checkout, and the webhook activates your account automatically.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="mb-3 flex items-center gap-2 text-slate-700">
                  <Shield className="h-4 w-4" />
                  <span className="text-sm font-semibold">Server-verified billing</span>
                </div>
                <p className="text-sm leading-6 text-slate-600">
                  Entitlements are written to the account after Stripe confirms payment — not from the browser redirect.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-4">
          {PLANS.map((plan) => {
            const isActive = plan.id === planId;
            const isLoading = loadingPlan === plan.id;
            return (
              <Card key={plan.id} className={`relative overflow-hidden ${plan.recommended ? "border-blue-300 shadow-md shadow-blue-100" : "border-slate-200"}`}>
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${plan.accent}`} />
                <CardHeader className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <CardDescription className="mt-1">{plan.description}</CardDescription>
                    </div>
                    {plan.recommended ? <Badge className="bg-blue-600 text-white hover:bg-blue-600">Recommended</Badge> : null}
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold tracking-tight">{plan.price}</span>
                    {plan.id !== "free" ? <span className="pb-1 text-sm text-slate-500">per month</span> : null}
                  </div>
                  <p className="text-sm text-slate-500">{plan.highlight}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <div className="px-6 pb-6">
                  <Button
                    className={plan.id === "free" ? "w-full" : "w-full bg-blue-600 text-white hover:bg-blue-700"}
                    variant={plan.id === "free" ? "outline" : "default"}
                    onClick={() => startCheckout(plan.id)}
                    disabled={isLoading || (isActive && plan.id !== "free")}
                  >
                    {isLoading ? "Opening checkout..." : isActive ? `Current: ${plan.name}` : plan.cta}
                  </Button>
                </div>
              </Card>
            );
          })}
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-center gap-2">
            <Crown className="h-5 w-5 text-blue-600" />
            <h2 className="text-xl font-semibold tracking-tight">What changes as you upgrade</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] border-separate border-spacing-y-3 text-left text-sm">
              <thead>
                <tr className="text-slate-500">
                  <th className="px-4 py-2 font-medium">Feature</th>
                  <th className="px-4 py-2 font-medium">Free</th>
                  <th className="px-4 py-2 font-medium">Foundation</th>
                  <th className="px-4 py-2 font-medium">Operator</th>
                  <th className="px-4 py-2 font-medium">Atlas</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.label} className="rounded-2xl bg-slate-50/80">
                    <td className="rounded-l-2xl px-4 py-3 font-medium text-slate-800">{row.label}</td>
                    <td className="px-4 py-3 text-slate-600">{row.free}</td>
                    <td className="px-4 py-3 text-slate-600">{row.foundation}</td>
                    <td className="px-4 py-3 text-slate-600">{row.operator}</td>
                    <td className="rounded-r-2xl px-4 py-3 text-slate-600">{row.atlas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How checkout works</CardTitle>
              <CardDescription>Everything is tied to the server, so billing state stays trustworthy.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-6 text-slate-600">
              <p>1. Choose a plan card on this page or from Settings.</p>
              <p>2. Stripe Checkout opens with the correct live price ID for that tier.</p>
              <p>3. Stripe confirms payment and the webhook updates your account plan automatically.</p>
              <p>4. Returning users see the active plan in Settings and in the app shell.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Need a quick path?</CardTitle>
              <CardDescription>Use the existing in-app route if you already know the tier you want.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-slate-600">
              <p>
                If you are already inside the app, you can still use Settings to start checkout immediately.
                The pricing page is just a cleaner entry point with the same Stripe flow.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => navigate("/settings")}>Open Settings</Button>
                <Button variant="outline" onClick={() => navigate(isAuthenticated ? "/" : "/login?redirect=/pricing")}>Continue browsing</Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
